/**
 * Row scale for `WorkshopList` — sm/md/lg, matching the design's three target
 * viewports (mobile / tablet / desktop). Kept as data rather than folded into
 * `WorkshopListItem` so the row's own render logic stays small.
 *
 * Box and type scale together: every text size on the row is read from here,
 * so a caller that asks for `lg` gets larger type, not just a larger box.
 *
 * The date block is the one exception. `DateBadge` draws it, and its
 * `stretched` variant carries the list's own scale for it — width, numeral,
 * labels and corner — so none of those are repeated here. Numbers nothing
 * reads are numbers that drift away from what renders.
 *
 * The two files are still related: the block's corner is this row's corner
 * less 4, which is how the design derives it. A test pins that relation, so
 * changing `radius` here without changing `DateBadge` fails rather than
 * quietly unsquaring the block.
 */

export type WorkshopListSize = 'sm' | 'md' | 'lg';

export interface WorkshopListSizeSpec {
  /** Card padding. */
  pad: number;
  /** Gap between the date block and the content column. */
  gap: number;
  /** Gap between rows in the list. */
  rowGap: number;
  radius: number;
  minH: number;
  /** Venue name. */
  venue: number;
  /** The sub-region and species line. */
  meta: number;
  /** The Teacher / Assistant lines. */
  staff: number;
  /** The time pill. */
  pill: number;
  /** The sold/cap count, and the tree seal that matches it. */
  count: number;
}

export const WORKSHOP_LIST_SIZES: Record<WorkshopListSize, WorkshopListSizeSpec> = {
  sm: {
    pad: 10,
    gap: 12,
    rowGap: 10,
    radius: 14,
    minH: 44,
    venue: 15,
    meta: 11,
    staff: 10.5,
    pill: 10.5,
    count: 10.5,
  },
  md: {
    pad: 12,
    gap: 14,
    rowGap: 12,
    radius: 15,
    minH: 48,
    venue: 16.5,
    meta: 12,
    staff: 11.5,
    pill: 11,
    count: 11,
  },
  lg: {
    pad: 14,
    gap: 16,
    rowGap: 14,
    radius: 16,
    minH: 56,
    venue: 18,
    meta: 13,
    staff: 12.5,
    pill: 12,
    count: 12,
  },
};
